import { Button, Divider, InputNumber, Select, Space } from 'antd';
import React from 'react';
import { useDebounce, useDebouncedCallback } from 'use-debounce';
import {
  CloseCircleOutlined,
  DownloadOutlined,
  LeftOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
  RightOutlined,
  VerticalLeftOutlined,
  VerticalRightOutlined,
} from '@ant-design/icons';
import classes from './Toolbar.module.scss';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

const sizes = [
  { value: 50, label: '50%' },
  { value: 75, label: '75%' },
  { value: 100, label: '100%' },
  { value: 125, label: '125%' },
  { value: 150, label: '150%' },
  { value: 175, label: '175%' },
  { value: 200, label: '200%' },
  { value: 300, label: '300%' },
  { value: 400, label: '400%' },
  { value: 500, label: '500%' },
];

type ToolbarType = {
  scale: number;
  setScale: React.Dispatch<React.SetStateAction<number>>;
  pagesCount: number;
  toolbarRef: React.RefCallback<Element>;
  scrollToItem: (value: number | null) => void;
  currentPage?: number;
  onClose?: () => void;
  downloadDocument?: () => void;
  isModal?: boolean;
  isMobile?: boolean;
  isStatic?: boolean;
};

const Toolbar: React.FC<ToolbarType> = ({
  scale,
  setScale,
  pagesCount,
  toolbarRef,
  scrollToItem,
  onClose,
  currentPage = 1,
  downloadDocument,
  isModal,
  isMobile,
  isStatic
}) => {
  const { t } = useTranslation();
  const [debouncedCurrentPage] = useDebounce(currentPage, 100);

  const pressEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    !isNaN(Number((e.target as HTMLInputElement).value))
      && scrollToItem(Number((e.target as HTMLInputElement).value));
  };

  const onLeaveToolbar = () => {
    const pagesInput = document.getElementById('input_pages');
    pagesInput?.blur();
  };

  const onZoomUp = () => setScale((prev) => ( prev <= 4.9 ) ? ( prev + 0.1 ) : prev);
  const onZoomDown = () => setScale((prev) => ( prev >= 0.2 ) ? ( prev - 0.1 ) : prev);
  const onChangeZoom = (_: any, item?: {[name:string]: any}) => setScale(+item?.value / 100);
  const onIncrementPage = () => scrollToItem(currentPage + 1);
  const onDecrementPage = () => scrollToItem(currentPage - 1);
  const onIncrementSlidePage = () => scrollToItem(currentPage + 10);
  const onDecrementSlidePage = () => scrollToItem(currentPage - 10);
  const debouncedOnChange = useDebouncedCallback(scrollToItem, 500)

  return (
    <div
      ref={toolbarRef}
      className={cn(classes.toolbar, isStatic ? classes.static : 'null')}
      onMouseLeave={onLeaveToolbar}  
    >
      <Divider orientation='vertical' className={classes.divider} />

      {!isMobile && (
        <>
          <Space>
            <VerticalRightOutlined onClick={onDecrementSlidePage} />
            <LeftOutlined onClick={onDecrementPage} />
            <RightOutlined onClick={onIncrementPage} />
            <VerticalLeftOutlined onClick={onIncrementSlidePage} />
            <InputNumber
              id='input_pages'
              className={classes.pageCount}
              value={debouncedCurrentPage}
              onStep={scrollToItem}
              min={1}
              max={pagesCount}
              onChange={debouncedOnChange}
              onPressEnter={pressEnter}
              size='small'
            />
            <b className={classes.bElem}>{t('main.of')} {pagesCount}</b>
          </Space>
          <Divider orientation='vertical' className={classes.divider} />
        </>
      )}

      <Space>
        <PlusCircleOutlined style={{fontSize: 20}} onClick={onZoomUp} />
        <MinusCircleOutlined style={{fontSize: 20}} onClick={onZoomDown} />

        <Select
          size='small'
          options={sizes}
          style={{ width: 90 }}
          popupStyle={{ zIndex: 9999 }}
          onChange={(_, o) => onChangeZoom(_, o)}
          value={{
            value: Math.round(scale*100),
            label: `${Math.round(scale*100)}%`
          }}
        />

      </Space>
      <Divider orientation='vertical' className={classes.divider} />

      {downloadDocument && <>
        <Button onClick={downloadDocument} className={classes.downloadBtn} icon={<DownloadOutlined />}>
          {t('templink.download')}
        </Button>
        <Divider orientation='vertical' className={classes.divider} />
      </>}
      {isModal && onClose && <CloseCircleOutlined className={classes.close} onClick={onClose} />}
    </div>
  );
};

export default Toolbar;
